import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiCameraOff } from "react-icons/fi";

const QrScannerModal = ({ open, onClose, onDetected }) => {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const lockRef = useRef(false);

  const [status, setStatus] = useState("initial"); // initial | scanning | detected | error
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (!open) return;

    lockRef.current = false;
    setStatus("scanning");
    setErrMsg("");

    readerRef.current = new BrowserMultiFormatReader();

    const start = async () => {
      try {
        await readerRef.current.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (result) => {
            if (!result || lockRef.current) return;

            lockRef.current = true;
            setStatus("detected");

            const text = result.getText();
            onDetected?.(text);

            // thoda sa UX delay so user feels "detected"
            setTimeout(() => {
              onClose?.();
            }, 250);
          }
        );
      } catch (e) {
        console.error(e);
        setStatus("error");
        setErrMsg(
          "Camera access blocked. Please allow camera permission and try again."
        );
      }
    };

    start();

    return () => {
      try {
        readerRef.current?.reset();
      } catch {}
      lockRef.current = false;
    };
  }, [open, onClose, onDetected]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-[2px] flex items-center justify-center px-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onMouseDown={(e) => {
          // click outside closes
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <motion.div
          initial={{ scale: 0.97, opacity: 0, y: 8 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.97, opacity: 0, y: 8 }}
          transition={{ duration: 0.18 }}
          className="bg-white rounded-2xl w-[92vw] max-w-md overflow-hidden shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div>
              <h3 className="font-semibold text-sm text-gray-900">Scan QR Code</h3>
              <p className="text-[11px] text-gray-500">
                Align the code inside the frame
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
              aria-label="Close"
              type="button"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Camera area */}
          <div className="relative bg-black">
            <div className="relative h-[320px] overflow-hidden">
              <video
                ref={videoRef}
               autoPlay
  muted
  playsInline
  className="w-full h-full object-cover"
              />

              {/* Dark overlay to make frame pop */}
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/10 via-transparent to-black/25" />

              {/* Frame */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="relative w-[78%] max-w-[280px] aspect-square">
                  {/* blur overlay outside frame */}
                  <div className="absolute -inset-[999px] bg-black/35" />

                  {/* frame border */}
                  <div className="absolute inset-0 rounded-2xl border border-white/60 shadow-[0_0_0_999px_rgba(0,0,0,0.35)]" />

                  {/* corner brackets */}
                  <span className="absolute -top-[2px] -left-[2px] w-8 h-8 border-l-4 border-t-4 border-white rounded-tl-2xl" />
                  <span className="absolute -top-[2px] -right-[2px] w-8 h-8 border-r-4 border-t-4 border-white rounded-tr-2xl" />
                  <span className="absolute -bottom-[2px] -left-[2px] w-8 h-8 border-l-4 border-b-4 border-white rounded-bl-2xl" />
                  <span className="absolute -bottom-[2px] -right-[2px] w-8 h-8 border-r-4 border-b-4 border-white rounded-br-2xl" />

                  {/* scan line */}
                  {status === "scanning" && (
                    <motion.div
                      className="absolute left-3 right-3 h-[2px] rounded-full bg-white/80 shadow-[0_0_12px_rgba(255,255,255,0.6)]"
                      initial={{ top: "10%" }}
                      animate={{ top: ["10%", "90%", "10%"] }}
                      transition={{
                        duration: 2.1,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                  )}

                  {/* center dot (optional) */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white/70 shadow-[0_0_10px_rgba(255,255,255,0.6)]" />
                  </div>
                </div>
              </div>

              {/* Bottom status */}
              <div className="absolute left-0 right-0 bottom-0 p-3">
                <div className="mx-auto w-fit px-3 py-2 rounded-xl bg-black/60 text-white flex items-center gap-2">
                  {status === "scanning" && (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
                      <span className="text-xs">Scanning…</span>
                    </>
                  )}

                  {status === "detected" && (
                    <>
                      <span className="inline-block w-2 h-2 rounded-full bg-white" />
                      <span className="text-xs">Detected ✓</span>
                    </>
                  )}

                  {status === "error" && (
                    <>
                      <FiCameraOff />
                      <span className="text-xs">
                        {errMsg || "Camera error"}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <p className="text-[11px] text-gray-500">
              Tip: Keep QR steady and in good light
            </p>
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QrScannerModal;
